import { Trans, useLingui } from "@lingui/react/macro";
import { useContext, useEffect, useRef, useState } from "react";
import Chat from "@medoma/components/Chat/Chat";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import * as Sentry from "@sentry/react";
import ChatContext from "@medoma/chat/ChatContext";
import { formatTimestamp, makeMessages } from "@medoma/chat/useMatrixEvents";
import { useSendReadReceiptOnSeen } from "@medoma/chat/useSendReadReceipt";
import { useMatrixRoom } from "@medoma/chat/useMatrixRoom";
import { sendTextMessage } from "@medoma/chat/matrixClient";
import { useChatScroll } from "@medoma/chat/useChatScroll";
import { SyncState } from "matrix-js-sdk";
import { useMatrixConnectionStatus } from "@medoma/chat/useMatrixConnectionStatus";
import { useMatrixTimelineEvents } from "@medoma/chat/useMatrixEvents";
import {
  lastEventReadByUserOtherThanUserId,
  useLastReadEventPerUser,
} from "@medoma/chat/useLastReadEvent";
import { getHistory } from "@medoma/chat/getHistory";
import { medicalTitle } from "@medoma/models/shifts";
import { useSendTypingNotification } from "@medoma/chat/useSendTypingNotification";
import { usePatientId } from "@/utils/usePatientId";
import { getPatientRoomAlias } from "@medoma/chat/roomAlias";
import { Text } from "@medoma/components/Text/Text";
import styles from "./chat.module.scss";
import { usePatient, type IPatient } from "@/api/patient";
import { getChatInfoMessages } from "./chatInfoMessages";

const ChatComponent = () => {
  const { data: patient, isPending, isError, error } = usePatient();
  const { t } = useLingui();

  if (isPending) {
    return <Loading message={t`Laddar chatt`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return <ErrorMessage message={t`Kunde inte ladda chatten`} />;
  }

  return <ChatContent patient={patient} />;
};

const ChatContent = ({ patient }: { patient: IPatient }) => {
  const [pendingMessage, setPendingMessage] = useState("");
  const { matrixClient } = useContext(ChatContext);
  const { t } = useLingui();
  const chatConnection = useMatrixConnectionStatus(matrixClient);
  const patientId = usePatientId();
  const roomAlias = getPatientRoomAlias({ patientId, matrixClient });
  const { roomId, getRoom } = useMatrixRoom(matrixClient, roomAlias);
  const userId = matrixClient?.getUserId() ?? undefined;

  const timelineEvents = useMatrixTimelineEvents(matrixClient, roomId);
  const { sendNoLongerTypingNotification, updateTypingState } =
    useSendTypingNotification({
      matrixClient,
      roomId,
    });

  // Send no longer typing notification when leaving chat
  useEffect(() => {
    return () => {
      sendNoLongerTypingNotification();
    };
  }, [sendNoLongerTypingNotification]);

  const lastReadEventBySomeNonPatientUser = lastEventReadByUserOtherThanUserId(
    useLastReadEventPerUser({ getRoom }),
    userId,
  );

  const chatInfoMessages = getChatInfoMessages(patient.site.id);

  const messages = userId ? makeMessages(timelineEvents, userId) : [];

  const latestMessage = messages[messages.length - 1]?.id ?? null;

  useSendReadReceiptOnSeen(matrixClient, timelineEvents, userId);

  const messagePanelRef = useRef<HTMLDivElement>(null);
  const inputTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const { scrollMessagePanelToBottom } = useChatScroll({
    messagePanelRef,
    inputTextAreaRef,
    messages,
  });

  const sendMessage = () => {
    if (!matrixClient || !roomId) {
      return;
    }
    sendTextMessage(matrixClient, roomId, pendingMessage, () =>
      setPendingMessage(""),
    );
    sendNoLongerTypingNotification();
  };

  const room = getRoom();
  return matrixClient && room ? (
    <Chat>
      <Chat.MessagePanel
        getHistory={() => getHistory(matrixClient, room)}
        ref={messagePanelRef}
      >
        {messages.map((message) => {
          return message.sender.isMe ? (
            <Chat.MessageGroup
              key={message.id}
              sender="me"
              status={message.status}
              timestamp={formatTimestamp(message.epochTimestamp)}
              readIndicator={
                message.id === lastReadEventBySomeNonPatientUser?.eventId
              }
              isLatestMessage={latestMessage === message.id}
            >
              <Chat.Message
                type="sent-by-me"
                message={message.text}
                enableClickableLinks={false}
              />
            </Chat.MessageGroup>
          ) : (
            <Chat.MessageGroup
              key={message.id}
              sender="other-person"
              senderName={message.sender.name ?? t`Din vårdgivare`}
              senderTitle={
                medicalTitle(message.sender.title, {
                  format: "long",
                }) ?? null
              }
              senderPhotoURL={message.sender.imageURL ?? null}
              status={message.status}
              timestamp={formatTimestamp(message.epochTimestamp)}
            >
              <Chat.Message
                type="received"
                message={message.text}
                enableClickableLinks={true}
              />
            </Chat.MessageGroup>
          );
        })}
      </Chat.MessagePanel>
      {chatConnection === SyncState.Error ? (
        <Chat.InfoPanel type="error">
          <Trans>Chatten fungerar inte just nu</Trans>
        </Chat.InfoPanel>
      ) : (
        <></>
      )}
      <Chat.InfoPanel type="info">
        <div className={styles.responseTimeInfo}>
          <Text element="div" size="small" weight="medium">
            {typeof chatInfoMessages.primary === "string"
              ? chatInfoMessages.primary
              : t(chatInfoMessages.primary)}
          </Text>
          <Text element="div" size="small">
            {typeof chatInfoMessages.secondary === "string"
              ? chatInfoMessages.secondary
              : t(chatInfoMessages.secondary)}
          </Text>
        </div>
      </Chat.InfoPanel>
      <Chat.InputPanel>
        <Chat.InputTextArea
          inputTextAreaRef={inputTextAreaRef}
          rows={3}
          value={pendingMessage}
          onChange={(event) => {
            setPendingMessage(event.currentTarget.value);
            updateTypingState({ event, pendingMessage });
          }}
          onFocus={scrollMessagePanelToBottom}
        />
        <Chat.SendMessageButton
          aria-label={t`Skicka`}
          disabled={pendingMessage.trim() === ""}
          onClick={sendMessage}
          size="medium"
        />
      </Chat.InputPanel>
    </Chat>
  ) : (
    <Loading message={t`Laddar chatt`} />
  );
};

export default ChatComponent;
